import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { Box, BottomNavigation, BottomNavigationAction, Tooltip } from '@mui/material';
import { Home, Info, Person, RestaurantMenu } from '@mui/icons-material';
import { RecipeProvider } from './context/RecipeContext';
import { useAuth } from './hooks/useAuth';
import Landing from './screens/Landing';
import Composer from './screens/Composer';
import Results from './screens/Results';
import RecipeCard from './screens/RecipeCard';
import CookMode from './screens/CookMode';
import About from './screens/About';
import Contact from './screens/Contact';
import PrivacyPolicy from './screens/PrivacyPolicy';
import TermsAndConditions from './screens/TermsAndConditions';
import FAQ from './screens/FAQ';
import Resources from './screens/Resources';
import Profile from './screens/Profile';
import Drinks from './screens/Drinks';
import KitchenToolkit from './screens/KitchenToolkit';
import DietaryGuide from './screens/DietaryGuide';
import GlobalCuisine from './screens/GlobalCuisine';
import CulinaryGlossary from './screens/CulinaryGlossary';
import Coffee from './screens/Coffee';
import Mediterranean from './screens/diets/Mediterranean';
import Flexitarian from './screens/diets/Flexitarian';
import Keto from './screens/diets/Keto';
import DASH from './screens/diets/DASH';
import MIND from './screens/diets/MIND';
import IntermittentFasting from './screens/diets/IntermittentFasting';
import Vegan from './screens/diets/Vegan';
import Paleo from './screens/diets/Paleo';
import LowFODMAP from './screens/diets/LowFODMAP';
import Whole30 from './screens/diets/Whole30';
import AntiInflammatory from './screens/diets/AntiInflammatory';
import GlutenFree from './screens/diets/GlutenFree';
import Footer from './components/Footer';

function AppContent() {
  const [value, setValue] = useState(0);
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();

  const handleNavigationChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/');
        break;
      case 1:
        navigate('/about');
        break;
      case 2:
        navigate('/dietary-guide');
        break;
      case 3:
        // Profile button now handles auth
        if (isAuthenticated) {
          navigate('/profile');
        } else {
          login();
        }
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ flexGrow: 1 }}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/composer" element={<Composer />} />
          <Route path="/results" element={<Results />} />
          <Route path="/popular" element={<Results />} />
          <Route path="/drinks" element={<Drinks />} />
          <Route path="/recipe/:id" element={<RecipeCard />} />
          <Route path="/cook-mode/:id" element={<CookMode />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/kitchen-toolkit" element={<KitchenToolkit />} />
          <Route path="/dietary-guide" element={<DietaryGuide />} />
          <Route path="/dietary-guide/mediterranean" element={<Mediterranean />} />
          <Route path="/dietary-guide/flexitarian" element={<Flexitarian />} />
          <Route path="/dietary-guide/keto" element={<Keto />} />
          <Route path="/dietary-guide/dash" element={<DASH />} />
          <Route path="/dietary-guide/mind" element={<MIND />} />
          <Route path="/dietary-guide/intermittent-fasting" element={<IntermittentFasting />} />
          <Route path="/dietary-guide/vegan" element={<Vegan />} />
          <Route path="/dietary-guide/paleo" element={<Paleo />} />
          <Route path="/dietary-guide/low-fodmap" element={<LowFODMAP />} />
          <Route path="/dietary-guide/whole30" element={<Whole30 />} />
          <Route path="/dietary-guide/anti-inflammatory" element={<AntiInflammatory />} />
          <Route path="/dietary-guide/gluten-free" element={<GlutenFree />} />
          <Route path="/global-cuisine" element={<GlobalCuisine />} />
          <Route path="/culinary-glossary" element={<CulinaryGlossary />} />
          <Route path="/dangers-of-coffee" element={<Coffee />} />
          <Route path="/profile" element={<Profile />} />
        </Routes>
      </Box>
      <Footer />
      <BottomNavigation
        value={value}
        onChange={handleNavigationChange}
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          display: { xs: 'flex', md: 'none' }
        }}
      >
        <Tooltip title="Go to Home">
          <BottomNavigationAction label="Home" icon={<Home />} />
        </Tooltip>
        <Tooltip title="About Us">
          <BottomNavigationAction label="About" icon={<Info />} />
        </Tooltip>
        <Tooltip title="Dietary Guide">
          <BottomNavigationAction label="Diets" icon={<RestaurantMenu />} />
        </Tooltip>
        <Tooltip title={isAuthenticated ? "My Profile" : "Log In"}>
          <BottomNavigationAction 
            label={isAuthenticated ? "Profile" : "Login"} 
            icon={<Person />} 
            sx={{
              color: isAuthenticated ? 'inherit' : 'primary.main',
              '& .MuiBottomNavigationAction-label': {
                color: isAuthenticated ? 'inherit' : 'primary.main',
                fontWeight: isAuthenticated ? 'normal' : 'bold',
              },
              '&.Mui-selected': {
                color: isAuthenticated ? 'inherit' : 'primary.main',
              },
            }}
          />
        </Tooltip>
      </BottomNavigation>
    </Box>
  );
}

function App() {
  return (
    <RecipeProvider>
      <Router>
        <AppContent />
      </Router>
    </RecipeProvider>
  );
}

export default App;
